<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PhotoFeature                                          |
| Section: components                                              |
| Purpose: Featured photo highlight block with contextual caption. |
+------------------------------------------------------------------+
-->
<template>
    <div class="PhotoFeature_container">
        <div class="PhotoFeature_wrapper">
            <TitleGroup class="TitleGroup" :label="$t('home.photoFeature.label')" :title="$t('home.photoFeature.title')"
                :isWhiteText="true" />
            <div class="photo_gallery" ref="photoGallery" :class="{ 'photo_gallery--dragging': isDragging }"
                @pointerdown="startGalleryDrag" @pointermove="onGalleryDrag" @pointerup="endGalleryDrag"
                @pointercancel="endGalleryDrag" @lostpointercapture="endGalleryDrag">
                <div v-for="photo in renderedPhotos" :key="photo.renderKey" class="photo_item"
                    @click="openOverlay(photo.originalIndex)">
                    <img :src="photo.url" alt="Featured Photo" draggable="false" />
                </div>
            </div>
            <div class="TitleBtnGroup">
                <h3>{{ $t('home.photoFeature.ctaTitle') }}</h3>
                <BtnPrimary class="BtnPrimary" :iconFillColor="'var(--brand-text-dark)'"
                    :label="$t('home.photoFeature.ctaButton')" href="/fotos-en-documenten?type=photos" />
            </div>
        </div>

        <!-- Media Overlay -->
        <MediaOverlay v-if="isOverlayVisible" :isVisible="isOverlayVisible" :mediaUrl="overlayMedia?.url"
            :mediaType="overlayMedia?.type" :mediaKind="overlayMedia?.kind" :mediaTitle="overlayMedia?.title"
            :mediaOwner="overlayMedia?.owner"
            :linkedPersonIDs="overlayMedia?.linkedPersonIDs" :mediaList="stablePhotos"
            :currentMediaIndex="currentPhotoIndex" :infiniteNavigation="true" @close="closeOverlay"
            @navigate="navigateOverlay" />

    </div>
</template>

<script>
import BtnPrimary from "@/atoms/BtnPrimary.vue";
import TitleGroup from "@/atoms/TitleGroup.vue";
import MediaOverlay from "@/components/MediaOverlay.vue";
import { normalizeOverlayMedia } from "@/utils/mediaUtils";

export default {
    components: {
        TitleGroup,
        BtnPrimary,
        MediaOverlay,
    },
    data() {
        return {
            photos: [
                {
                    filename: "featured_photo_1.jpg",
                    title: "Duncan met Everine, zoontje Frans en de buren Boedy",
                    mediaOwner: "Silvia Magdalena Jacobsz",
                    linkedPersonIDs: ["I82", "I83", "I15"]
                },
                {
                    filename: "featured_photo_2.jpg",
                    title: "Alexandrien Frederique Westhoff",
                    mediaOwner: "Winny Thümann",
                    linkedPersonIDs: ["I16"]
                },
                {
                    filename: "featured_photo_3.jpg",
                    title: "Angeline Amaly Jacobsz en Charles Jozef Thümann achter 't huis in de van Pijkerenlaan 29 te Oldebroek",
                    mediaOwner: "Jeffrey Thümann",
                    linkedPersonIDs: ["I1", "I2"]
                },
                {
                    filename: "featured_photo_4.jpg",
                    title: "Bob Westhoff, zijn vrouw Ida Berthram, en twee van hun kinderen waarvan de rechter Tjennie is (Jeanny Aleida Westhoff)",
                    mediaOwner: "Jeanny Westhoff",
                    linkedPersonIDs: ["I646", "I598", "I147", "I599"]
                },
                {
                    filename: "featured_photo_5.jpg",
                    title: "Angeline Amaly Jacobsz op Xcelsior motor van Francis Duncan Jacobsz en hond",
                    mediaOwner: "Rutger Dercks",
                    linkedPersonIDs: ["I2"]
                },
                {
                    filename: "featured_photo_6.jpg",
                    title: "Charles Jozef Thümann thuis bij de van Pijkerenlaan 29 te Oldebroek in 1984 zit op de motor van Rudy Thümann",
                    mediaOwner: "Rudy Thümann",
                    linkedPersonIDs: ["I1"]
                },
                {
                    filename: "featured_photo_7.jpg",
                    title: "Charles Jozef Thümann, Angeline Amaly Jacobsz en Rudy Thümann den 16den Januari 1976, 25 jaar getrouwd",
                    mediaOwner: "Angela Verheul",
                    linkedPersonIDs: ["I1", "I2", "I3"]
                },
                {
                    filename: "featured_photo_8.jpg",
                    title: "Van links naar rechts Jacky, Charles Jozef Thümann, Guus, Ruddi en Jan Thümann. Josina Akolo, Max Thümann, Meiske (Irene Thümann), Ida Munster, Max. Oom Freddy Thümann ayo! Jam karet voor de foto 3 min te laat",
                    mediaOwner: "Jeffrey Thümann",
                    linkedPersonIDs: ["I59", "I10", "I480", "I1", "I58", "I482", "I65", "I60", "I481"]
                },
                {
                    filename: "featured_photo_9.jpg",
                    title: "Charles Jozef Thümann precies achter de man met de bal, met de zichtbare tanden. v.v. Hollandia Kampioen 1938 op Borneo (Tarakkan) kustartillerie Peninki C.J. Thümann van Pijkerenlaan 29 Oldebroek (Gld.)",
                    mediaOwner: "Winny Thümann",
                    linkedPersonIDs: ["I1"]
                },
                {
                    filename: "featured_photo_10.jpg",
                    title: "Elius Jacobsz en Wilhelmina de Jong",
                    mediaOwner: "George de la Croix",
                    linkedPersonIDs: ["I594", "I175"]
                },
                {
                    filename: "featured_photo_11.jpg",
                    title: "Van links naar rechts Alexandrien Frederique Westhoff, Angeline Amaly Jacobsz, Charles Jozef Thümann, en een onbekende dame tijdens de bruiloft van de middelste twee in januari 1951",
                    mediaOwner: "Bert Otterspeer",
                    linkedPersonIDs: ["I1", "I2", "I16"]
                },
                {
                    filename: "featured_photo_12.jpg",
                    title: "Freddy Thümann met zijn vrouw Anie",
                    mediaOwner: "Stefan Thümann",
                    linkedPersonIDs: ["I95", "I61"]
                },
                {
                    filename: "featured_photo_13.jpg",
                    title: "Marcella, Irene en Meiske (van links naar rechts)",
                    mediaOwner: "Silvia Magdalena Jacobsz",
                    linkedPersonIDs: ["I85", "I88", "I84"]
                },
                {
                    filename: "featured_photo_14.jpg",
                    title: "Oom Ruud met fiets rond 1954",
                    mediaOwner: "William Verduyn Lunel",
                    linkedPersonIDs: ["I59"]
                },
                {
                    filename: "featured_photo_15.jpg",
                    title: "Een souvenirtje van je vriendin D. Jacobsz 10 October 1909; Studioportret van Dora Jacobsz op Java – Leiden University Libraries (KITLV 178316)",
                    mediaOwner: "Leiden University Libraries (KITLV 178316)",
                    linkedPersonIDs: ["I1446"]
                },
                {
                    filename: "featured_photo_16.jpg",
                    title: "Schoggras, Thümann, Isa, Klinkenberg",
                    mediaOwner: "Rutger Dercks",
                    linkedPersonIDs: ["I1"]
                },
                {
                    filename: "featured_photo_17.jpg",
                    title: "Rudy Thümann thuis met zijn motor",
                    mediaOwner: "Rudy Thümann",
                    linkedPersonIDs: ["I3"]
                },
            ],
            scrollPosition: 0,
            isOverlayVisible: false,
            currentPhotoIndex: 0,
            stablePhotos: [],
            galleryScrollOffset: 0,
            galleryDragOffset: 0,
            galleryCurrentOffset: 0,
            galleryCycleWidth: 0,
            galleryScrollNudgeMultiplier: 0.1,
            cloneCount: 3,
            dragStartX: 0,
            dragStartOffset: 0,
            dragPointerId: null,
            hasDraggedGallery: false,
            isDragging: false,
        };
    },
    computed: {
        renderedPhotos() {
            if (!this.stablePhotos.length) return [];

            const leadingClones = this.stablePhotos.slice(-this.cloneCount).map((photo, index) => ({
                ...photo,
                renderKey: `prepend_${photo.originalIndex}_${index}`
            }));
            const mainPhotos = this.stablePhotos.map((photo, index) => ({
                ...photo,
                renderKey: `main_${photo.originalIndex}_${index}`
            }));
            const trailingClones = this.stablePhotos.slice(0, this.cloneCount).map((photo, index) => ({
                ...photo,
                renderKey: `append_${photo.originalIndex}_${index}`
            }));

            return [...leadingClones, ...mainPhotos, ...trailingClones];
        },
        overlayMedia() {
            return normalizeOverlayMedia(this.stablePhotos[this.currentPhotoIndex], {
                type: 'image',
                kind: 'photo',
                defaultTitle: 'Untitled'
            });
        }
    },
    mounted() {
        this.stablePhotos = this.getStableShuffledPhotos();
        window.addEventListener("scroll", this.handleScroll);
        window.addEventListener("resize", this.handleGalleryResize);

        this.$nextTick(() => {
            this.measureGallery();
            this.applyGalleryPosition(this.getGalleryTotalOffset());
        });
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.handleScroll);
        window.removeEventListener("resize", this.handleGalleryResize);
    },
    methods: {
        getStableShuffledPhotos() {
            return [...this.photos]
                .map((photo, index) => ({
                    url: require(`@/assets/featured_photos/${photo.filename}`),
                    title: photo.title,
                    originalIndex: index,
                    linkedPersonIDs: photo.linkedPersonIDs,
                    mediaOwner: photo.mediaOwner
                }))
                .sort(() => Math.random() - 0.5)
                .map((photo, index) => ({
                    ...photo,
                    originalIndex: index
                }));
        },
        handleScroll() {
            const scrollY = window.scrollY;
            const maxScroll =
                document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = scrollY / maxScroll;

            // Set the scroll position for the gallery
            this.scrollPosition = scrollPercent * 100;
            this.updateGalleryPosition(scrollPercent);
        },
        updateGalleryPosition(scrollPercent) {
            if (!this.$refs.photoGallery) return;

            this.measureGallery();

            this.galleryScrollOffset = this.galleryCycleWidth * scrollPercent * this.galleryScrollNudgeMultiplier;
            this.applyGalleryPosition(this.getGalleryTotalOffset());
        },
        measureGallery() {
            const photoGallery = this.$refs.photoGallery;
            if (!photoGallery || !this.stablePhotos.length) return;

            const galleryItems = Array.from(photoGallery.children);
            const firstMainIndex = this.cloneCount;
            const firstMainItem = galleryItems[firstMainIndex];
            const firstTrailingClone = galleryItems[firstMainIndex + this.stablePhotos.length];

            if (!firstMainItem || !firstTrailingClone) return;

            const firstMainLeft = firstMainItem.offsetLeft;
            this.galleryCycleWidth = firstTrailingClone.offsetLeft - firstMainLeft;
        },
        applyGalleryPosition(offset, withTransition = true) {
            const photoGallery = this.$refs.photoGallery;
            if (!photoGallery) return;

            if (!this.galleryCycleWidth) {
                this.measureGallery();
            }

            const normalizedOffset = this.normalizeGalleryOffset(offset);
            this.galleryCurrentOffset = normalizedOffset;
            photoGallery.style.transition = withTransition ? '' : 'none';
            photoGallery.style.transform = `translateX(-${normalizedOffset}px)`;

            if (!withTransition) {
                photoGallery.offsetHeight;
                photoGallery.style.transition = '';
            }
        },
        normalizeGalleryOffset(offset) {
            if (!this.galleryCycleWidth) return Math.max(0, offset);

            const cycleWidth = this.galleryCycleWidth;
            const normalizedOffset = ((offset % cycleWidth) + cycleWidth) % cycleWidth;
            this.galleryDragOffset = normalizedOffset - this.galleryScrollOffset;
            return normalizedOffset;
        },
        getGalleryTotalOffset() {
            return this.galleryScrollOffset + this.galleryDragOffset;
        },
        startGalleryDrag(event) {
            if (event.button !== undefined && event.button !== 0) return;

            this.measureGallery();
            this.isDragging = true;
            this.hasDraggedGallery = false;
            this.dragPointerId = event.pointerId;
            this.dragStartX = event.clientX;
            this.dragStartOffset = this.getGalleryTotalOffset();
        },
        onGalleryDrag(event) {
            if (!this.isDragging || event.pointerId !== this.dragPointerId) return;

            const dragDelta = event.clientX - this.dragStartX;
            if (Math.abs(dragDelta) > 6) {
                this.hasDraggedGallery = true;
                event.preventDefault();

                if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) {
                    event.currentTarget.setPointerCapture(event.pointerId);
                }
            }

            this.galleryDragOffset = this.dragStartOffset - dragDelta - this.galleryScrollOffset;
            this.applyGalleryPosition(this.getGalleryTotalOffset(), false);
        },
        endGalleryDrag(event) {
            if (!this.isDragging || event.pointerId !== this.dragPointerId) return;

            this.isDragging = false;

            if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }

            this.dragPointerId = null;
            this.applyGalleryPosition(this.getGalleryTotalOffset());

            window.setTimeout(() => {
                this.hasDraggedGallery = false;
            }, 0);
        },
        handleGalleryResize() {
            this.measureGallery();
            this.applyGalleryPosition(this.getGalleryTotalOffset(), false);
        },
        openOverlay(index) {
            if (this.hasDraggedGallery) return;

            this.currentPhotoIndex = index;
            this.isOverlayVisible = true;
        },
        closeOverlay() {
            this.isOverlayVisible = false;
        },
        navigateOverlay(newIndex) {
            if (newIndex >= 0 && newIndex < this.stablePhotos.length) {
                this.currentPhotoIndex = newIndex;
            }
        }
    },
};
</script>

<style scoped>
.PhotoFeature_container {
    /* background: url(http://localhost:8080/img/noise.4e216b96.png), var(--brand-bg-3); */
    /* Using the alias (~@ resolves to src/) */
    background: url('@/assets/noise128x128.png'), var(--brand-bg-3);
    background-repeat: repeat;
    background-size: auto;
    background-position: center, center;
    overflow: hidden;
}

.PhotoFeature_wrapper {
    padding: var(--page-padding);
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-self: stretch;
}

.TitleGroup {
    margin: auto;
    max-width: 1280px;
}

.photo_gallery {
    display: flex;
    gap: 24px;
    overflow: visible;
    white-space: nowrap;
    background: rgba(0, 0, 0, 0);
    transform: translateX(0%);
    transition: var(--transition-bounce);
    touch-action: pan-y;
    user-select: none;
}

.photo_gallery--dragging {
    cursor: grabbing;
}

.photo_item {
    flex: 0 0 auto;
    width: auto;
    height: 302px;
}

.photo_item img {
    height: 100%;
    transition: var(--transition-bounce);
}

.photo_item img:hover {
    transform: scale(1.1);
    cursor: pointer;
}

.TitleBtnGroup {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: var(--brand-text-light);
    margin-top: 40px;
}

.BtnPrimary {
    background-color: var(--brand-bg-1);
    color: var(--brand-text-dark);
    border: 2px solid var(--brand-bg-1);
    box-shadow: 0px 0px 0px 0px var(--brand-bg-1);
}

.BtnPrimary:hover {
    box-shadow: 0px 0px 0px 4px var(--brand-bg-1);
}

.BtnPrimary {
    background-color: var(--brand-color-3);
    color: var(--brand-text-dark);
    border: 2px solid var(--brand-color-3);
    box-shadow: 0px 0px 0px 0px var(--brand-color-3);
}

.BtnPrimary:hover {
    box-shadow: 0px 0px 0px 4px var(--brand-color-3);
}

@media (max-width: 860px) {
    .PhotoFeature_wrapper {
        padding: var(--page-padding-mobile);
    }
}
</style>
